import { ArrowLeftIcon, cn, Label } from '@quickmd-llc/design-system'
import { useEffect, useRef, type ReactNode } from 'react'
import { useInert } from '../useInert'

type Props = {
  isOpen: boolean
  onClose: () => void
  title: ReactNode
  /** Optional metadata shown opposite the title, e.g. a submitted date. */
  headerRight?: ReactNode
  /** Overrides the body padding for panels whose rows own their own insets. */
  bodyClassName?: string
  children: ReactNode
}

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'

/**
 * Slide-in panel covering the visit popup's patient-info column. Stays mounted and animates on
 * `translate-x` so the content keeps its scroll position across open/close; the column it sits in
 * is `relative … overflow-hidden`, which is what bounds the `absolute inset-0`.
 *
 * The header is locked and the body scrolls — `min-h-0` is load-bearing, since every ancestor is a
 * flex column and would otherwise let the body grow past the panel instead of scrolling inside it.
 *
 * While open the panel traps Tab. That is not politeness: it is opaque and covers the column, and
 * on a video visit the Daily controls behind it include end-call. Without the trap those stay
 * reachable by keyboard through a surface the user cannot see.
 */
export const VisitSidePanel = ({ isOpen, onClose, title, headerRight, bodyClassName, children }: Props) => {
  const panelRef = useRef<HTMLDivElement>(null)
  const backRef = useRef<HTMLButtonElement>(null)
  const returnFocusRef = useRef<HTMLElement | null>(null)

  useInert(panelRef, !isOpen)

  useEffect(() => {
    if (!isOpen) return undefined

    const previouslyFocused = document.activeElement
    returnFocusRef.current = previouslyFocused instanceof HTMLElement ? previouslyFocused : null
    backRef.current?.focus()

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
        return
      }
      if (event.key !== 'Tab') return

      const panel = panelRef.current
      if (!panel) return
      const focusable = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE))
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (!first || !last) return

      const active = document.activeElement
      const leavingBackwards = event.shiftKey && (active === first || !panel.contains(active))
      const leavingForwards = !event.shiftKey && (active === last || !panel.contains(active))
      if (leavingBackwards) {
        event.preventDefault()
        last.focus()
      } else if (leavingForwards) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      returnFocusRef.current?.focus()
    }
  }, [isOpen, onClose])

  return (
    <div
      ref={panelRef}
      className={cn(
        'absolute inset-0 z-20 flex flex-col bg-white transition-transform duration-300 ease-in-out',
        isOpen ? 'translate-x-0' : '-translate-x-full',
      )}
      aria-hidden={!isOpen}>
      {/*
        The arrow is its own control and the title is plain text, matching the design and keeping
        the button's accessible name ("Back") off the visible heading — a single button labelled
        "Back" but reading "Alerts (4)" fails WCAG 2.5.3 Label in Name.
      */}
      <div className='flex shrink-0 items-center justify-between gap-sp-1 border-b border-border-medium px-sp-2 py-sp-1.5'>
        <div className='flex items-center gap-sp-1'>
          <button
            ref={backRef}
            type='button'
            onClick={onClose}
            aria-label='Back'
            className='flex items-center rounded text-text-dark focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-highlight'>
            <ArrowLeftIcon />
          </button>
          <Label size='lg' className='text-text-dark'>
            {title}
          </Label>
        </div>
        {headerRight}
      </div>

      <div className={cn('min-h-0 flex-1 overflow-y-auto', bodyClassName ?? 'px-sp-2 py-sp-2')}>{children}</div>
    </div>
  )
}
